import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	performGetRequest,
	performPostRequest,
	performPutRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	AdminSettingsRequest,
	AdminSettingsResponse,
	AdminSettingsMap,
	BannerSettings,
	AssetsLogoResponse,
	AssetsFaviconResponse,
} from '../common/types.tsx';
import {
	getAdminSettingsUrl,
	getBannerSettingsUrl,
	getLogoUrl,
	getFaviconUrl,
	transformAdminSettingsResponse,
	transformBannerSettings,
	mergeOptionsWithDefaults,
	SSR_SERVICE_NAME,
} from '../common/utils.tsx';
import {
	emptyBannerSettingsResponse,
	emptyLogoResponse,
	emptyFaviconResponse,
} from '../common/constants.tsx';

const packageName = 'jiraRouterResourcesAssetsAdminSettings';
const teamName = 'aurum';

/**
 * Fetches admin settings by keys for a specific workspace
 * Based on POST /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/settings
 */
export const getAdminSettingsByKeys = async (
	workspaceId: string,
	settingKeys: AdminSettingsRequest[],
): Promise<AdminSettingsMap> => {
	const url = getAdminSettingsUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		body: JSON.stringify(settingKeys),
		headers: {
			'x-ssr-service-name': SSR_SERVICE_NAME,
			'atl-workspaceid': workspaceId,
		},
	});

	try {
		const response = await performPostRequest<AdminSettingsResponse>(url, mergedOptions);

		return transformAdminSettingsResponse(response);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode === 403) {
			return {};
		}

		fireErrorAnalytics({
			meta: {
				id: 'getAdminSettingsByKeys',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Fetches banner settings for a specific workspace
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/banner-settings
 */
export const getBannerSettings = async (workspaceId: string): Promise<BannerSettings> => {
	const url = getBannerSettingsUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		headers: {
			'x-ssr-service-name': 'jsm-cmdb-gateway',
			'atl-workspaceid': workspaceId,
		},
	});

	try {
		const response = await performGetRequest<BannerSettings>(url, mergedOptions);

		return transformBannerSettings(response);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode === 403 || error.statusCode === 404) {
			return emptyBannerSettingsResponse;
		}

		fireErrorAnalytics({
			meta: {
				id: 'getBannerSettings',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Updates banner settings for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/banner-settings/<banner-id>
 */
export const updateBannerSettings = async (
	workspaceId: string,
	bannerId: string,
	bannerUpdates: BannerSettings,
): Promise<BannerSettings> => {
	const url = `${getBannerSettingsUrl(workspaceId)}/${bannerId}`;

	try {
		const response = await performPutRequest<BannerSettings>(
			url,
			mergeOptionsWithDefaults(url, {
				body: JSON.stringify(transformBannerSettings(bannerUpdates)),
			}),
		);
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'updateBannerSettings',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Updates navigation settings for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/settings
 */
export const updateNavigationSettings = async (
	workspaceId: string,
	navigationUpdates: AdminSettingsResponse,
) => {
	const url = getAdminSettingsUrl(workspaceId);

	try {
		return await performPutRequest<AdminSettingsMap>(
			url,
			mergeOptionsWithDefaults(url, {
				body: JSON.stringify(navigationUpdates),
			}),
		);
	} catch (error: unknown) {
		const err = error instanceof Error ? error : new Error(String(error));
		fireErrorAnalytics({
			meta: {
				id: 'updateNavigationSettings',
				packageName,
				teamName,
			},
			error: err,
		});
		throw err;
	}
};

/**
 * Fetches logo for a specific workspace
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/assets-logo
 */
export const getLogo = async (workspaceId: string): Promise<AssetsLogoResponse> => {
	const url = getLogoUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		headers: {
			'x-ssr-service-name': SSR_SERVICE_NAME,
			'atl-workspaceid': workspaceId,
		},
	});

	try {
		const response = await performGetRequest<AssetsLogoResponse>(url, mergedOptions);
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode === 204 || error.statusCode === 403 || error.statusCode === 404) {
			return emptyLogoResponse;
		}

		fireErrorAnalytics({
			meta: {
				id: 'getLogo',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Fetches favicon for a specific workspace
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/favicon
 */
export const getFavicon = async (workspaceId: string): Promise<AssetsFaviconResponse> => {
	const url = getFaviconUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		headers: {
			'x-ssr-service-name': SSR_SERVICE_NAME,
			'atl-workspaceid': workspaceId,
		},
	});

	try {
		const response = await performGetRequest<AssetsFaviconResponse>(url, mergedOptions);
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode === 204 || error.statusCode === 403 || error.statusCode === 404) {
			return emptyFaviconResponse;
		}

		fireErrorAnalytics({
			meta: {
				id: 'getFavicon',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Uploads logo for a specific workspace
 * Based on POST /gateway/api/jsm/assets/workspace/<workspace-id>/v1/assets-logo/upload
 */
export const uploadLogo = async (
	workspaceId: string,
	payload: { file: File; fileName: string },
): Promise<AssetsLogoResponse> => {
	try {
		const url = `${getLogoUrl(workspaceId)}/upload`;
		const formData = new FormData();
		formData.append('assets-logo-file', payload.file);
		formData.append('fileName', payload.fileName);

		const response = await performPostRequest<AssetsLogoResponse>(url, {
			body: formData,
			headers: {},
		});
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'uploadLogo',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Uploads favicon for a specific workspace
 * Based on POST /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/favicon/upload
 */
export const uploadFavicon = async (
	workspaceId: string,
	payload: { file: File; fileName: string },
): Promise<AssetsFaviconResponse> => {
	try {
		const url = `${getFaviconUrl(workspaceId)}/upload`;
		const formData = new FormData();
		formData.append('assets-favicon-file', payload.file);
		formData.append('fileName', payload.fileName);

		const response = await performPostRequest<AssetsFaviconResponse>(url, {
			body: formData,
			headers: {},
		});
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'uploadFavicon',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Resets logo to default for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/assets-logo/reset-to-default
 */
export const resetLogoToDefault = async (workspaceId: string): Promise<AssetsLogoResponse> => {
	try {
		const url = `${getLogoUrl(workspaceId)}/reset-to-default`;
		const response = await performPutRequest<AssetsLogoResponse>(url);
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'resetLogoToDefault',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

/**
 * Resets favicon to default for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/favicon/reset-to-default
 */
export const resetFaviconToDefault = async (
	workspaceId: string,
): Promise<AssetsFaviconResponse> => {
	try {
		const url = `${getFaviconUrl(workspaceId)}/reset-to-default`;
		const response = await performPutRequest<AssetsFaviconResponse>(url);
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'resetFaviconToDefault',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};
